import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { resolvePreset } from 'utils/executive.utils'
import type {
  DrillLevel,
  ExecutiveFilterState,
  DatePreset,
} from 'api/executive/executive.types'

const LOCAL_STORAGE_KEY = 'EXECUTIVE_DASHBOARD_FILTERS'

// 'this-month' range via the shared timezone-safe resolver. The previous
// inline impl formatted local Date objects with toISOString(), which emits
// UTC — in Asia/Bangkok (UTC+7) that shifted both ends back a day, so
// "this month" became e.g. 2026-04-30→2026-05-30 instead of 05-01→05-31.
const defaultDateRange = (): ExecutiveFilterState['dateRange'] => ({
  preset: 'this-month',
  ...resolvePreset('this-month')!, // non-'custom' presets never resolve to null
})

const loadFromLocalStorage = (): ExecutiveFilterState | null => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ExecutiveFilterState
    // Re-resolve non-'custom' presets against today so a saved range always
    // reflects the live period (a 'this-month' saved in May means June when
    // reopened in June) and picks up date-logic fixes — this also repairs any
    // stale/UTC-shifted range persisted before the fix above.
    const preset = parsed.dateRange?.preset
    if (preset && preset !== 'custom') {
      const range = resolvePreset(preset)
      if (range) parsed.dateRange = { preset, ...range }
    }
    return parsed
  } catch {
    return null
  }
}

// Pristine defaults. Kept separate from `initialFilters` (which may come from
// localStorage) so "ล้างตัวกรอง" really clears instead of restoring whatever
// was persisted at page load (MD-2609-1161 TC-06: a saved doctorId survived
// reset on tabs whose drawer has no doctor field).
export const DEFAULT_EXECUTIVE_FILTERS: ExecutiveFilterState = {
  dateRange: defaultDateRange(),
  branchId: null,
  doctorId: null,
  procedureId: null,
  txCategoryId: null,
  patientType: null,
  appointmentStatus: null,
  paymentType: null,
  nationality: null,
  sourceChannel: null,
  promotionId: null,
}

const initialFilters: ExecutiveFilterState = loadFromLocalStorage() ?? DEFAULT_EXECUTIVE_FILTERS

export interface ExecutiveState {
  filters: ExecutiveFilterState
  drillStack: DrillLevel[]
  lastRefreshAt: number | null
  loadingWidgets: Record<string, boolean>
}

const initialState: ExecutiveState = {
  filters: initialFilters,
  drillStack: [],
  lastRefreshAt: null,
  loadingWidgets: {},
}

const persist = (state: ExecutiveState) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state.filters))
  } catch {
    // ignore quota errors
  }
}

export const executiveSlice = createSlice({
  name: 'executive',
  initialState,
  reducers: {
    setDateRange: (
      state,
      action: PayloadAction<{ preset: DatePreset; start: string; end: string }>
    ) => {
      state.filters.dateRange = action.payload
      persist(state)
    },
    setFilter: <K extends keyof ExecutiveFilterState>(
      state: ExecutiveState,
      action: PayloadAction<{ key: K; value: ExecutiveFilterState[K] }>
    ) => {
      state.filters[action.payload.key] = action.payload.value
      persist(state)
    },
    resetFilters: (state) => {
      // "ล้างตัวกรอง" clears every advanced (chip) filter — including ones the
      // current tab's drawer does not show — but leaves the shared bar alone:
      // date preset and branch are separate concerns on the main bar.
      state.filters = {
        ...DEFAULT_EXECUTIVE_FILTERS,
        dateRange: state.filters.dateRange,
        branchId: state.filters.branchId,
      }
      persist(state)
    },
    pushDrill: (state, action: PayloadAction<DrillLevel>) => {
      state.drillStack.push(action.payload)
    },
    popDrill: (state) => {
      state.drillStack.pop()
    },
    clearDrill: (state) => {
      state.drillStack = []
    },
    markRefreshed: (state) => {
      state.lastRefreshAt = Date.now()
    },
    setWidgetLoading: (
      state,
      action: PayloadAction<{ key: string; loading: boolean }>
    ) => {
      const { key, loading } = action.payload
      if (loading) {
        state.loadingWidgets[key] = true
      } else {
        delete state.loadingWidgets[key]
      }
    },
  },
})

export const {
  setDateRange,
  setFilter,
  resetFilters,
  pushDrill,
  popDrill,
  clearDrill,
  markRefreshed,
  setWidgetLoading,
} = executiveSlice.actions

export default executiveSlice.reducer
